import * as THREE from "three";
import {
    CBTextureLoader,
    CBTextureLoadError,
    CBTextureLoadErrorReason,
    getGlobalCanvas,
    nearestPowerOf2
} from "./CBTextureLoader";
import {CBTextureType, SuperpixelData} from "./CBTypes";
import {CanvasContext} from "react-three-fiber";

export class CBSceneProperties {

    constructor(public readonly backgroundUrl:string,
                public readonly lightingUrl?:string,
                public readonly superpixelsUrl?:string,
                public masks?:{[type: string]: string},
                public anchorPoint?:[number,number]|undefined) {

    }

    //ancorPoint -1 to 1, north and south from [0,0] origin
}

function removeElement(element:HTMLElement) {
    if (element.parentNode) {
        element.parentNode.removeChild(element);
    }
}

export class CBSuperpixelsImage {

    public readonly canvas = getGlobalCanvas("superpixels");
    public readonly labels: { [label: string]: SuperpixelData } = {};

    constructor(img: HTMLImageElement) {
        this.canvas.width = nearestPowerOf2(img.width);
        this.canvas.height = nearestPowerOf2(img.height);
        const ctx = this.canvas.getContext("2d");
        if (ctx) {
            ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);

            const labelData: { [label: string]: SuperpixelData } = {};

            const data = ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
            for (let x = 0; x < this.canvas.width; x++) {
                for (let y = 0; y < this.canvas.height; y++) {
                    const rgba = [
                        data[this.canvas.width * 4 * y + 4 * x],
                        data[this.canvas.width * 4 * y + 4 * x + 1],
                        data[this.canvas.width * 4 * y + 4 * x + 2],
                        data[this.canvas.width * 4 * y + 4 * x + 3],
                    ].toString();

                    const relPos: [number, number] = [
                        x / this.canvas.width,
                        y / this.canvas.height
                    ];

                    const labelDatum = labelData[rgba];
                    if (labelDatum) {
                        labelDatum.pixels.push(relPos);
                        labelDatum.center[0] += relPos[0];
                        labelDatum.center[1] += relPos[1]
                    } else {
                        labelData[rgba] = {
                            pixels: [relPos],
                            meanRadius: 0,
                            center: [relPos[0], relPos[1]]
                        }
                    }
                }
            }

            // Calculate centers and mean radii
            for (const label in labelData) {
                const labelDatum = labelData[label];
                labelDatum.center[0] /= labelDatum.pixels.length;
                labelDatum.center[1] /= labelDatum.pixels.length;

                for (const pixel of labelDatum.pixels) {
                    labelDatum.meanRadius += Math.sqrt(Math.pow(pixel[0] - labelDatum.center[0], 2) + Math.pow(pixel[1] - labelDatum.center[1], 2))
                }
                labelDatum.meanRadius /= labelDatum.pixels.length
            }
            this.labels = labelData
        }
    }

    public dispose() {
        removeElement(this.canvas)
    }
}

export class CBMask {
    public memoryCanvas = getGlobalCanvas("mask");
    public readonly maskTexture:THREE.CanvasTexture;

    constructor(img:HTMLImageElement) {
        this.memoryCanvas.width = nearestPowerOf2(img.width);
        this.memoryCanvas.height = nearestPowerOf2(img.height);
        this.maskTexture = new THREE.CanvasTexture(this.memoryCanvas);
        const ctx = this.memoryCanvas.getContext("2d");
        if (ctx) {
            ctx.drawImage(img, 0, 0, this.memoryCanvas.width, this.memoryCanvas.height);
            this.maskTexture.needsUpdate = true
        }
    }

    public dispose() {
        removeElement(this.memoryCanvas);
        const image = this.maskTexture.image as HTMLElement;
        if (image) {
            removeElement(image)
        }

        this.maskTexture.dispose()
    }
}

export class CBScene {
    public width:number = 0;
    public height:number = 0;

    public backgroundTexture?:THREE.Texture|undefined;
    public lightingTexture?:THREE.Texture|undefined;
    public superpixels?:CBSuperpixelsImage|undefined;
    public masks:Array<CBMask> = [];

    private _numNeedLoading = 0;

    constructor(public readonly props:CBSceneProperties, public readonly context:CanvasContext) {

    }

    public load(onLoad: () => void,
                onError: (event: CBTextureLoadError) => void) {

        this._numNeedLoading = 0;
        if (this.props.backgroundUrl) {
            this.loadTexture(this.props.backgroundUrl, CBTextureType.Background, getGlobalCanvas("background"), (texture:THREE.Texture, width:number, height:number) => {
                this.backgroundTexture = texture;
                this.width = width;
                this.height = height
            }, onLoad, onError)
        }

        if (this.props.lightingUrl) {
            this.loadTexture(this.props.lightingUrl, CBTextureType.Lighting, getGlobalCanvas("lighting"), (texture:THREE.Texture) => {
                this.lightingTexture = texture
            }, onLoad, onError)
        }

        if (this.props.superpixelsUrl) {
            this.loadImage(this.props.superpixelsUrl, CBTextureType.Superpixels, (image:HTMLImageElement) => {
                this.superpixels = new CBSuperpixelsImage(image)
            }, onLoad, onError)
        }

        for (let key in this.props.masks) {
            this.loadImage(this.props.masks[key], CBTextureType.Mask, (image:HTMLImageElement) => {
                this.masks.push(new CBMask(image))
            }, onLoad, onError)
        }
    }

    private loadImage(url:string, type:CBTextureType,
                      onLoadImage:(image:HTMLImageElement) => void,
                      onLoadScene:() => void,
                      onError: (event: CBTextureLoadError) => void) {

        this._numNeedLoading ++;
        const image = new Image();
        image.onerror = () => {
            onError(new CBTextureLoadError(type, url, CBTextureLoadErrorReason.InvalidUrl))
        };
        image.onload = (e:Event) => {
            const img = e.target as HTMLImageElement;
            if (img) {
                onLoadImage(img);
                this._numNeedLoading --;
                if (!this._numNeedLoading) {
                    onLoadScene()
                }
            } else {
                onError(new CBTextureLoadError(type, url, CBTextureLoadErrorReason.InvalidUrl))
            }
        };
        image.crossOrigin = "";
        image.src = url
    }

    private loadTexture(url:string, type:CBTextureType, canvas:HTMLCanvasElement,
                        onLoadTexture:(texture:THREE.Texture, width:number, height:number) => void,
                        onLoadScene:() => void,
                        onError: (event: CBTextureLoadError) => void) {

        this._numNeedLoading ++;
        new CBTextureLoader(this.context).load(type, url, canvas,(texture:THREE.Texture, width:number, height:number) => {
            onLoadTexture(texture, width, height);
            this._numNeedLoading --;
            if (!this._numNeedLoading) {
                onLoadScene()
            }
        }, undefined, onError)
    }

    public dispose() {
        //console.log("Disposing of scene");

        if (this.backgroundTexture) {
            this.backgroundTexture.dispose()
        }

        if (this.lightingTexture) {
            this.lightingTexture.dispose()
        }

        if (this.superpixels) {
            this.superpixels.dispose()
        }

        for (let mask of this.masks) {
            mask.dispose()
        }
    }
}
